import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API_ENDPOINTS } from '../config/api';

const Updates = () => {
  const [updates, setUpdates] = useState([]);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [heroImages, setHeroImages] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    fetchUpdates();
    fetchHeroImages();
    
    // Auto-slide hero images
    const heroInterval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % (heroImages.length || 1));
    }, 5000);

    return () => {
      clearInterval(heroInterval);
    };
  }, [filter, heroImages.length]);

  const fetchHeroImages = async () => {
    try {
      const response = await axios.get(API_ENDPOINTS.HERO_IMAGES, {
        params: { is_active: 1 }
      });
      if (response.data.success) {
        setHeroImages(response.data.data.map(img => img.image_path));
      } else {
        setHeroImages([
          '/images/hero1.jpg',
          '/images/hero2.jpg',
          '/images/hero3.jpg'
        ]);
      }
    } catch (error) {
      console.error('Error fetching hero images:', error);
      setHeroImages([
        '/images/hero1.jpg',
        '/images/hero2.jpg',
        '/images/hero3.jpg'
      ]);
    }
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  const fetchUpdates = async () => {
    try {
      setLoading(true);
      let params = {};
      if (filter !== 'all') {
        params.type = filter;
      }
      
      const response = await axios.get(API_ENDPOINTS.UPDATES, { params });
      if (response.data.success) {
        setUpdates(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching updates:', error);
      // Fallback data for development
      setUpdates([
        {
          id: 1,
          title: 'New Partnership with International Organization',
          excerpt: 'SIKOM partners with an international organization to expand peacebuilding efforts in the North Rift region.',
          content: 'We are excited to announce a new partnership that will significantly enhance our peacebuilding initiatives...',
          type: 'news',
          created_at: '2024-03-15',
          views: 125,
          slug: 'new-partnership-international-organization'
        },
        {
          id: 2,
          title: 'Community Workshop Success Story',
          excerpt: 'Over 100 community members trained in conflict resolution techniques in West Pokot.',
          content: 'Last week, SIKOM conducted a successful workshop on conflict resolution...',
          type: 'blog',
          created_at: '2024-03-10',
          views: 89,
          slug: 'community-workshop-success-story'
        },
        {
          id: 3,
          title: 'Annual Report 2023 Released',
          excerpt: 'Our annual report highlights key achievements and impact from the past year.',
          content: 'We are pleased to share our annual report for 2023, showcasing our work...',
          type: 'news',
          created_at: '2024-02-28',
          views: 210,
          slug: 'annual-report-2023-released'
        },
        {
          id: 4,
          title: 'Photo Gallery: Field Visit to Turkana',
          excerpt: 'Photos from our recent field visit to humanitarian projects in Turkana County.',
          content: 'A collection of photos from our field visit showcasing our work...',
          type: 'gallery',
          created_at: '2024-02-20',
          views: 156,
          slug: 'photo-gallery-field-visit-turkana'
        },
        {
          id: 5,
          title: 'Upcoming: Peace Conference 2024',
          excerpt: 'Join us for our annual peace conference bringing together stakeholders from across the region.',
          content: 'We are organizing the annual peace conference to discuss solutions...',
          type: 'event',
          created_at: '2024-03-01',
          views: 95,
          slug: 'upcoming-peace-conference-2024'
        },
        {
          id: 6,
          title: 'Livelihoods Program Expansion',
          excerpt: 'Our sustainable livelihoods program expands to two new counties.',
          content: 'We are expanding our livelihoods program to reach more communities...',
          type: 'news',
          created_at: '2024-02-15',
          views: 112,
          slug: 'livelihoods-program-expansion'
        },
        {
          id: 7,
          title: 'Impact of Climate Change on Pastoral Communities',
          excerpt: 'A blog post examining how climate change affects pastoral communities.',
          content: 'Climate change has significantly impacted pastoral communities...',
          type: 'blog',
          created_at: '2024-02-05',
          views: 78,
          slug: 'climate-change-pastoral-communities'
        },
        {
          id: 8,
          title: 'Volunteer Appreciation Day',
          excerpt: 'Celebrating our dedicated volunteers who make our work possible.',
          content: 'We recently held a volunteer appreciation day to thank our amazing team...',
          type: 'event',
          created_at: '2024-01-30',
          views: 145,
          slug: 'volunteer-appreciation-day'
        },
        {
          id: 9,
          title: 'New Health Center Inauguration',
          excerpt: 'Opening of a new community health center in Samburu County.',
          content: 'We are proud to announce the opening of a new health center...',
          type: 'news',
          created_at: '2024-01-25',
          views: 189,
          slug: 'new-health-center-inauguration'
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const getTypeIcon = (type) => {
    switch(type) {
      case 'news': return 'bi-newspaper';
      case 'blog': return 'bi-pencil';
      case 'gallery': return 'bi-images';
      case 'event': return 'bi-calendar-event';
      default: return 'bi-file-text';
    }
  };

  const getTypeLabel = (type) => {
    switch(type) {
      case 'news': return 'News';
      case 'blog': return 'Blog';
      case 'gallery': return 'Gallery';
      case 'event': return 'Event';
      default: return 'Update';
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Filter updates based on selected filter
  const filteredUpdates = filter === 'all' 
    ? updates 
    : updates.filter(update => update.type === filter);

  return (
    <div className="updates-page">
      <style>{`
        .hero-section {
          min-height: 60vh;
          position: relative;
          overflow: hidden;
        }

        .hero-slider {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          z-index: 0;
        }

        .hero-slide {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background-size: cover;
          background-position: center;
          transition: opacity 1.5s ease-in-out, transform 8s ease-out;
        }

        .hero-slide.active {
          animation: kenBurns 8s ease-out;
        }

        @keyframes kenBurns {
          0% { transform: scale(1); }
          100% { transform: scale(1.1); }
        }

        .slider-dot {
          width: 12px;
          height: 12px;
          border-radius: 50%;
          border: 2px solid white;
          background: transparent;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .slider-dot.active {
          background: white;
          transform: scale(1.2);
        }
      `}</style>
      
      {/* Hero Section */}
      <section className="hero-section position-relative text-white">
        {/* Background Image Slider */}
        <div className="hero-slider">
          {heroImages.map((image, index) => (
            <div
              key={index}
              className={`hero-slide ${index === currentSlide ? 'active' : ''}`}
              style={{
                backgroundImage: `url(${image})`,
                opacity: index === currentSlide ? 1 : 0,
                transform: index === currentSlide ? 'scale(1)' : 'scale(1.1)',
              }}
            />
          ))}
          
          {/* Overlay */}
          <div 
            className="position-absolute top-0 start-0 w-100 h-100"
            style={{
              background: 'linear-gradient(135deg, rgba(13, 110, 253, 0.85) 0%, rgba(108, 117, 125, 0.75) 100%)'
            }}
          />
        </div>

        {/* Content */}
        <div className="container position-relative" style={{ zIndex: 2, paddingTop: '100px', paddingBottom: '100px' }}>
          <div className="row">
            <div className="col-lg-8">
              <h1 className="display-3 fw-bold text-white mb-4">News & Updates</h1>
              <p className="lead text-white mb-0">
                Stay informed about our latest activities, news, and events
              </p>
            </div>
          </div>
        </div>

        {/* Slider Indicators */}
        <div 
          className="position-absolute bottom-0 start-50 translate-middle-x mb-4"
          style={{ zIndex: 3 }}
        >
          <div className="d-flex gap-2">
            {heroImages.map((_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                className={`slider-dot ${index === currentSlide ? 'active' : ''}`}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Filter Section */}
      <section className="py-4 bg-light">
        <div className="container">
          <div className="row">
            <div className="col-12">
              <div className="d-flex flex-wrap justify-content-center gap-2">
                <button 
                  className={`btn ${filter === 'all' ? 'btn-primary' : 'btn-outline-primary'} rounded-pill px-4`}
                  onClick={() => setFilter('all')}
                >
                  All Updates
                </button>
                <button 
                  className={`btn ${filter === 'news' ? 'btn-primary' : 'btn-outline-primary'} rounded-pill px-4`}
                  onClick={() => setFilter('news')}
                >
                  <i className="bi bi-newspaper me-2"></i> News
                </button>
                <button 
                  className={`btn ${filter === 'blog' ? 'btn-primary' : 'btn-outline-primary'} rounded-pill px-4`}
                  onClick={() => setFilter('blog')}
                >
                  <i className="bi bi-pencil me-2"></i> Blog
                </button>
                <button 
                  className={`btn ${filter === 'gallery' ? 'btn-primary' : 'btn-outline-primary'} rounded-pill px-4`}
                  onClick={() => setFilter('gallery')}
                >
                  <i className="bi bi-images me-2"></i> Gallery
                </button>
                <button 
                  className={`btn ${filter === 'event' ? 'btn-primary' : 'btn-outline-primary'} rounded-pill px-4`}
                  onClick={() => setFilter('event')}
                >
                  <i className="bi bi-calendar-event me-2"></i> Events
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Updates Grid */}
      <section className="section-padding">
        <div className="container">
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : filteredUpdates.length === 0 ? (
            <div className="text-center py-5">
              <i className="bi bi-newspaper display-1 text-muted mb-4"></i>
              <h3>No updates available</h3>
              <p className="text-muted mb-4">
                {filter !== 'all' ? `No ${filter} updates at the moment.` : 'Check back later for new updates'}
              </p>
              {filter !== 'all' && (
                <button 
                  className="btn btn-primary mt-3"
                  onClick={() => setFilter('all')}
                >
                  View All Updates
                </button>
              )}
            </div>
          ) : (
            <div className="row g-4">
              {filteredUpdates.map((update) => (
                <div className="col-lg-4 col-md-6" key={update.id}>
                  <div className="card border-0 shadow-sm hover-card h-100">
                    <div className="card-body d-flex flex-column">
                      <div className="mb-3">
                        <span className={`badge ${update.type === 'news' ? 'bg-primary' : update.type === 'blog' ? 'bg-success' : update.type === 'event' ? 'bg-warning text-dark' : 'bg-info'} p-2`}>
                          <i className={`bi ${getTypeIcon(update.type)} me-1`}></i>
                          {getTypeLabel(update.type)}
                        </span>
                      </div>
                      
                      <h5 className="card-title">{update.title}</h5>
                      <p className="card-text text-muted flex-grow-1">
                        {update.excerpt || (update.content ? update.content.substring(0, 150) + '...' : 'Read more about this update...')}
                      </p>
                      
                      <div className="d-flex justify-content-between align-items-center mt-4 pt-3 border-top">
                        <div className="d-flex align-items-center">
                          <small className="text-muted">
                            <i className="bi bi-calendar me-1"></i>
                            {formatDate(update.created_at)}
                          </small>
                          {(update.views || update.views === 0) && (
                            <small className="text-muted ms-3">
                              <i className="bi bi-eye me-1"></i>
                              {update.views}
                            </small>
                          )}
                        </div>
                        
                        <Link 
                          to={`/updates/${update.slug || update.id}`}
                          className="btn btn-link text-decoration-none d-flex align-items-center"
                        >
                          Read More <i className="bi bi-arrow-right ms-1"></i>
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Newsletter Subscription */}
      <section className="py-5 bg-primary text-white">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-6">
              <h2 className="display-5 fw-bold mb-3">Stay Updated</h2>
              <p className="lead mb-4">
                Subscribe to our newsletter and never miss important updates
              </p>
              <div className="d-flex align-items-center">
                <i className="bi bi-envelope display-6 me-3"></i>
                <div>
                  <h5>Email Updates</h5>
                  <p className="mb-0">Get updates directly in your inbox</p>
                </div>
              </div>
            </div>
            <div className="col-lg-6">
              <div className="card bg-dark bg-opacity-25 border-0">
                <div className="card-body p-4">
                  <form className="newsletter-form" onSubmit={(e) => {
                    e.preventDefault();
                    const email = e.target.querySelector('input[type="email"]').value;
                    alert(`Thank you for subscribing with email: ${email}`);
                    e.target.reset();
                  }}>
                    <div className="mb-3">
                      <label htmlFor="newsletterEmail" className="form-label">Your Email Address</label>
                      <div className="input-group">
                        <input 
                          type="email" 
                          className="form-control form-control-lg" 
                          id="newsletterEmail"
                          placeholder="name@example.com"
                          required
                        />
                        <button className="btn btn-light btn-lg" type="submit">
                          Subscribe
                        </button>
                      </div>
                    </div>
                    <small className="form-text text-white-50">
                      By subscribing, you agree to receive updates from SIKOM. We respect your privacy and will never share your information.
                    </small>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Social Media Links */}
      <section className="py-5 bg-light">
        <div className="container">
          <div className="row text-center">
            <div className="col-12 mb-4">
              <h2 className="display-5 fw-bold mb-4">Follow Us</h2>
              <p className="lead mb-5">Stay connected through our social media channels</p>
            </div>
            <div className="col-lg-8 mx-auto">
              <div className="d-flex justify-content-center flex-wrap gap-4">
                <a href="#" className="text-decoration-none">
                  <div className="d-flex flex-column align-items-center">
                    <div className="bg-primary text-white rounded-circle p-4 mb-2">
                      <i className="bi bi-facebook display-6"></i>
                    </div>
                    <span className="fw-medium">Facebook</span>
                  </div>
                </a>
                <a href="#" className="text-decoration-none">
                  <div className="d-flex flex-column align-items-center">
                    <div className="bg-info text-white rounded-circle p-4 mb-2">
                      <i className="bi bi-twitter display-6"></i>
                    </div>
                    <span className="fw-medium">Twitter</span>
                  </div>
                </a>
                <a href="#" className="text-decoration-none">
                  <div className="d-flex flex-column align-items-center">
                    <div className="bg-danger text-white rounded-circle p-4 mb-2">
                      <i className="bi bi-youtube display-6"></i>
                    </div>
                    <span className="fw-medium">YouTube</span>
                  </div>
                </a>
                <a href="#" className="text-decoration-none">
                  <div className="d-flex flex-column align-items-center">
                    <div className="bg-success text-white rounded-circle p-4 mb-2">
                      <i className="bi bi-whatsapp display-6"></i>
                    </div>
                    <span className="fw-medium">WhatsApp</span>
                  </div>
                </a>
                <a href="#" className="text-decoration-none">
                  <div className="d-flex flex-column align-items-center">
                    <div className="bg-dark text-white rounded-circle p-4 mb-2">
                      <i className="bi bi-linkedin display-6"></i>
                    </div>
                    <span className="fw-medium">LinkedIn</span>
                  </div>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Updates;